import axios from "axios"
import { basePath, withBase } from "constants/basePath"
import {
  getErrorMessage,
  isCancelledError,
  normalizeApiError,
} from "helpers/error_helpers"

const TOKEN_KEY = "authUser"

export function setToken(token) {
  localStorage.setItem(TOKEN_KEY, JSON.stringify(token))
}

export function deleteToken() {
  localStorage.removeItem(TOKEN_KEY)
}

export function getToken() {
  try {
    return JSON.parse(localStorage.getItem(TOKEN_KEY))
  } catch (error) {
    console.log(error)
  }
}

// Decodifica el payload de un JWT sin verificar la firma (eso lo hace el
// backend). Solo sirve para leer claims como "exp" y detectar del lado del
// cliente un token vencido o corrupto antes de gastar un request.
export function decodeJwtPayload(accessToken) {
  try {
    const payload = accessToken.split(".")[1]
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/")
    return JSON.parse(atob(base64))
  } catch {
    return null
  }
}

// BDSJEA-130: un token puede existir en localStorage y aun asi ser invalido
// para esta sesion -- vencido, corrupto, o de otro ambiente (dev/qa/prod)
// guardado en la misma PC. hasToken() por si sola no alcanza.
export function isTokenValid() {
  const token = getToken()
  if (!token?.accessToken) return false

  const payload = decodeJwtPayload(token.accessToken)
  if (!payload?.exp) return false

  return payload.exp * 1000 > Date.now()
}

// Limpia cualquier estado de sesion invalido (token vencido, corrupto, o de
// otro ambiente). No navega -- eso queda a cargo de quien la llama, para no
// forzar un window.location dentro de un render de React.
export function clearSession() {
  deleteToken()
  localStorage.clear()
}

// Para usar fuera del arbol de React (p.ej. el interceptor de axios, que
// corre antes de que exista cualquier componente): limpia la sesion y manda
// al login. Preserva la URL que el usuario pedia para volver ahi despues.
export function redirectToLogin() {
  const current = window.location.pathname + window.location.search
  if (current.includes("/iniciar-sesion")) return

  clearSession()
  const base = basePath()
  const from = encodeURIComponent(
    base ? current.replace(new RegExp(`^${base}`), "") : current,
  )
  window.location.href = withBase(`/iniciar-sesion?from=${from}`)
}

export const customAxios = axios.create({
  baseUrl: `${window.REACT_APP_URL}/ewanauth/v1/login`,
})

customAxios.interceptors.response.use(
  response => response,
  error => {
    const msg = error.response?.data?.message
    switch (msg) {
      case "Access Denied":
        throw new Error("Usuario o password incorrectos")
      case "INVALID_CREDENTIALS":
        throw new Error("Usuario o password incorrectos")
      case "industrial.com.ar:389":
        throw new Error("No se pudo conectar con el servidor de autenticación")
      case "USER_DISABLED":
        throw new Error("El usuario está deshabilitado")
      default:
        // BDSJEA-312: un 5xx / timeout / red caida no es "contraseña
        // incorrecta": se informa el problema real. Con respuesta < 500 se
        // conserva el texto de login (incluye el 401 de credenciales malas).
        if (isCancelledError(error)) throw error
        if (!error.response || error.response.status >= 500) {
          throw new Error(msg || getErrorMessage(error))
        }
        throw new Error(msg || "Usuario o contraseña incorrectos")
    }
  },
)

export default customAxios

export function initAxiosInterceptors() {
  axios.interceptors.request.use(async config => {
    var token = getToken()

    if (token) {
      config.headers.Authorization = `Bearer ${token.accessToken}`
    }

    if (config.url.indexOf("refreshToken") > 0) {
      config.headers.Authorization = `Bearer ${token.accessToken}`
      config.headers["Authorization-Refresh"] = `Bearer ${token.refreshToken}`
    }

    if (config.url.indexOf("appconfig") > 0) {
      config.headers.Authorization = null
    }

    return config
  })

  // BDSJEA-130: esta era la pieza que faltaba. Sin este interceptor, un 401
  // por JWT vencido/invalido (incluido un token de otro ambiente que decodifica
  // bien del lado del cliente pero falla la firma en el backend) quedaba sin
  // manejar: cada pantalla mostraba su propio error de conexion y el usuario
  // tenia que ir a "Cerrar sesion" a mano.
  axios.interceptors.response.use(
    response => response,
    error => {
      // BDSJEA-312: mensaje descriptivo para el usuario en error.message (la
      // cancelacion no se toca). Va ANTES del redirect de 401 porque este
      // limpia la sesion y el texto del 401 depende de si habia sesion.
      // No se muestra ningun toast aca: eso lo decide cada pantalla via
      // toastHandler.setError (muchas atrapan el error a proposito).
      if (!isCancelledError(error)) normalizeApiError(error)

      if (error?.response?.status === 401 && getToken()) {
        redirectToLogin()
      }
      // BDSJEA-35: un 403 no es una sesion invalida, es un permiso que falta.
      // Se distingue del 401 a proposito: deslogear al usuario por entrar a una
      // pantalla que no le corresponde seria peor que decirselo.
      if (error?.response?.status === 403) {
        error.accesoDenegado = true
      }
      return Promise.reject(error)
    },
  )
}

// BDSJEA-35: los roles se leen del propio JWT, que es la misma fuente que usa
// el backend para autorizar. Asi la UI y la API no pueden discrepar: si el
// token dice ROLE_CONSULTA, el boton no aparece y el endpoint devuelve 403.
// Decodificar sin verificar la firma alcanza porque esto solo decide que se
// dibuja; la decision que importa la toma el backend.
export function getRolesFromToken() {
  const token = getToken()
  if (!token?.accessToken) return []

  const payload = decodeJwtPayload(token.accessToken)
  const roles = payload?.roles
  return Array.isArray(roles) ? roles : []
}
